"use client";

import React from 'react';
import { Quote, Star } from 'lucide-react';
import { useTranslations } from 'next-intl';

const TestimonialsSection: React.FC = () => {
  const t = useTranslations();

  const reviews = [
    {
      text: t('review_1_seo'),
      author: t('review_1_author'),
      role: t('review_1_role_seo'),
      avatar: "https://i.pravatar.cc/100?img=1",
      rating: 5
    },
    {
      text: t('review_2_seo'),
      author: t('review_2_author'),
      role: t('review_2_role_seo'),
      avatar: "https://i.pravatar.cc/100?img=3",
      rating: 5
    },
    {
      text: t('review_3_seo'),
      author: t('review_3_author'),
      role: t('review_3_role_seo'),
      avatar: "https://i.pravatar.cc/100?img=5",
      rating: 5
    }
  ];

  // Schema.org structured data for reviews
  const serviceData = {
    "@type": "Service",
    "name": t('Header.title'),
    "url": "https://kinofolio.com",
    "provider": {
      "@type": "Organization",
      "name": t('Header.title'),
      "url": "https://kinofolio.com"
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": t('Header.title'),
    "url": "https://kinofolio.com",
    "provider": {
      "@type": "Organization",
      "name": t('Header.title'),
      "url": "https://kinofolio.com"
    },
    "review": reviews.map(review => ({
      "@type": "Review",
      "itemReviewed": serviceData,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating,
        "bestRating": 5
      },
      "author": {
        "@type": "Person",
        "name": review.author,
        "jobTitle": review.role
      },
      "reviewBody": review.text
    })),
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": reviews.length
    }
  };

  return (
    <section className="py-16 md:py-24 bg-background text-foreground overflow-hidden relative" id="testimonials">
      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      
      <div className="container-std">
        {/* Header */}
        <div className="text-center mb-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary mb-6">
            <span className="text-xs font-bold uppercase tracking-wider">
              {t('testimonials_badge')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-foreground tracking-tight">
            {t('testimonials_heading_seo')}
          </h2>
          <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('testimonials_subheading')}
          </p>
        </div>

        {/* Testimonial Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {reviews.map((review, idx) => (
            <div
              key={idx}
              className="glass-card p-6 md:p-8 rounded-[24px] border border-white/10 flex flex-col animate-in fade-in slide-in-from-bottom-8 duration-700"
              style={{ animationDelay: `${idx * 100}ms` }}
              itemScope
              itemType="https://schema.org/Review"
            >
              {/* Quote Icon */}
              <div className="text-primary/20 mb-4">
                <Quote size={32} fill="currentColor" />
              </div>

              {/* Stars */}
              <div className="flex gap-1 mb-4" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
                <meta itemProp="ratingValue" content={String(review.rating)} />
                <meta itemProp="bestRating" content="5" />
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className={i < review.rating ? "text-yellow-400" : "text-gray-600"} fill={i < review.rating ? "currentColor" : "none"} />
                ))}
              </div>

              {/* Review Text */}
              <p className="text-foreground text-lg leading-relaxed mb-8 flex-1" itemProp="reviewBody">
                &ldquo;{review.text}&rdquo;
              </p>

              {/* Author */}
              <div className="flex items-center gap-4 pt-6 border-t border-white/10" itemProp="author" itemScope itemType="https://schema.org/Person">
                <img
                  src={review.avatar}
                  alt={review.author}
                  loading="lazy"
                  width={48}
                  height={48}
                  className="w-12 h-12 rounded-full object-cover border-2 border-white/10"
                  itemProp="image"
                />
                <div>
                  <p className="font-bold text-foreground" itemProp="name">{review.author}</p>
                  <p className="text-sm text-muted-foreground" itemProp="jobTitle">{review.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
